<script setup lang="ts">
import { onMounted, computed, ref } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import { useThreads } from '@/composables/useThreads'
import { useChat } from '@/composables/useChat'
import TypingDots from '@/components/chat/TypingDots.vue'
import type { ThreadSummary } from '@/types'

const {
  threads, activeThreadId, loadingThreads,
  refreshThreads, newThread, deleteThread, renameThread,
} = useThreads()
const { openThread: openThreadFlow, isThreadStreaming } = useChat()

onMounted(() => {
  refreshThreads()
})

async function openThread(thread: ThreadSummary) {
  if (thread.thread_id === activeThreadId.value) return
  await openThreadFlow(thread.thread_id)
}

function formatTime(iso: string): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    const now = new Date()
    const sameDay = d.toDateString() === now.toDateString()
    if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

// --- Age grouping (Today / Yesterday / Previous 7 Days / ... / Month YYYY) ----
interface ThreadGroup { label: string; threads: ThreadSummary[] }

const DAY_MS = 86_400_000

function groupLabel(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return 'Earlier'
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const t = d.getTime()
  if (t >= startOfToday) return 'Today'
  if (t >= startOfToday - DAY_MS) return 'Yesterday'
  if (t >= startOfToday - 7 * DAY_MS) return 'Previous 7 Days'
  if (t >= startOfToday - 30 * DAY_MS) return 'Previous 30 Days'
  return d.toLocaleDateString([], { month: 'long', year: 'numeric' })
}

// Threads arrive ordered by updated_at desc, so first-appearance ordering of
// the buckets follows recency (Today → Yesterday → … → older months).
const groupedThreads = computed<ThreadGroup[]>(() => {
  const groups: ThreadGroup[] = []
  const byLabel = new Map<string, ThreadGroup>()
  for (const t of threads.value) {
    const label = groupLabel(t.updated_at)
    let g = byLabel.get(label)
    if (!g) {
      g = { label, threads: [] }
      byLabel.set(label, g)
      groups.push(g)
    }
    g.threads.push(t)
  }
  return groups
})

// --- Per-thread Rename / Delete menu ----------------------------------------
const menuFor = ref<string | null>(null)
const renamingId = ref<string | null>(null)
const renameText = ref('')
// Function refs in a v-for run on every re-render (each keystroke updates
// renameText → re-render). Guard so we only focus+select once per rename
// session; otherwise select() re-runs after each keystroke and the next
// character overwrites the re-selected text.
const renameInputFocused = ref(false)

// Function ref: focus + select the rename input the first time it mounts (more
// reliable than a shared template ref inside a v-for).
function focusRenameInput(el: Element | ComponentPublicInstance | null) {
  if (el instanceof HTMLInputElement && renamingId.value && !renameInputFocused.value) {
    el.focus()
    el.select()
    renameInputFocused.value = true
  }
}

function closeMenu() {
  menuFor.value = null
}

// Open the options popover upward when there isn't room below (otherwise it
// falls off the bottom of the viewport for the last threads in the list).
const menuDirection = ref<'down' | 'up'>('down')

function toggleMenu(id: string, event: MouseEvent) {
  if (menuFor.value === id) {
    menuFor.value = null
    return
  }
  const btn = event.currentTarget as HTMLElement | null
  if (btn) {
    const rect = btn.getBoundingClientRect()
    // Popover is ~84px tall (two rows); flip up if less than that below.
    menuDirection.value = window.innerHeight - rect.bottom < 96 ? 'up' : 'down'
  }
  menuFor.value = id
}

function startRename(t: ThreadSummary) {
  menuFor.value = null
  renamingId.value = t.thread_id
  renameText.value = t.name || ''
  renameInputFocused.value = false
}

async function commitRename(t: ThreadSummary) {
  const name = renameText.value.trim()
  const id = renamingId.value
  renamingId.value = null
  if (!id || !name || name === (t.name || '')) return
  try {
    await renameThread(id, name)
  } catch (e) {
    console.error('[ThreadList] rename failed:', e)
  }
}

function cancelRename() {
  renamingId.value = null
}

async function confirmDelete(t: ThreadSummary) {
  menuFor.value = null
  const label = t.name || 'this conversation'
  if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return
  try {
    await deleteThread(t.thread_id)
  } catch (e) {
    console.error('[ThreadList] delete failed:', e)
  }
}
</script>

<template>
  <aside class="flex flex-col bg-neutral-50 border-r border-neutral-200 overflow-hidden">
    <div class="flex items-center justify-between px-3 py-2.5 min-h-[48px] border-b border-neutral-200 shrink-0">
      <h2 class="text-[11px] uppercase tracking-wider font-semibold text-neutral-500">
        Conversations
      </h2>
      <button
        class="px-2 py-1 text-[11px] bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        @click="newThread"
      >
        + New
      </button>
    </div>

    <div class="flex-1 overflow-y-auto">
      <div v-if="loadingThreads" class="text-center text-xs text-neutral-400 py-6">
        Loading…
      </div>
      <div v-else-if="threads.length === 0" class="text-center text-xs text-neutral-400 py-6 px-3">
        No conversations yet. Send a message to start one.
      </div>
      <template v-else>
        <!-- Click-away backdrop for the open row menu. -->
        <div v-if="menuFor" class="fixed inset-0 z-10" @click="closeMenu" />

        <div v-for="group in groupedThreads" :key="group.label" class="py-1">
          <div class="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wider font-semibold text-neutral-400">
            {{ group.label }}
          </div>
          <ul>
            <li
              v-for="thread in group.threads"
              :key="thread.thread_id"
              class="relative group/item"
            >
              <!-- Inline rename -->
              <div
                v-if="renamingId === thread.thread_id"
                class="px-3 py-2 border-l-[3px] border-blue-500 bg-white"
              >
                <input
                  :ref="focusRenameInput"
                  v-model="renameText"
                  type="text"
                  class="w-full rounded border border-neutral-300 px-2 py-1 text-[13px] text-neutral-800 focus:border-blue-500 focus:outline-none"
                  @keydown.enter.prevent="commitRename(thread)"
                  @keydown.esc.prevent="cancelRename"
                  @blur="commitRename(thread)"
                />
              </div>

              <!-- Normal row -->
              <div
                v-else
                class="flex items-stretch border-l-[3px] transition-colors hover:bg-white"
                :class="thread.thread_id === activeThreadId
                  ? 'bg-white border-blue-500'
                  : 'border-transparent'"
              >
                <button
                  class="min-w-0 flex-1 text-left px-3 py-2"
                  @click="openThread(thread)"
                >
                  <div class="text-[13px] text-neutral-800 font-medium truncate">
                    {{ thread.name || `Thread ${thread.thread_id.slice(0, 8)}` }}
                  </div>
                  <div class="flex items-center gap-2 mt-0.5">
                    <TypingDots
                      v-if="isThreadStreaming(thread.thread_id)"
                      :size="5"
                      color-class="bg-blue-500"
                    />
                    <template v-else>
                      <span class="text-[11px] text-neutral-400">
                        {{ formatTime(thread.updated_at) }}
                      </span>
                      <span
                        v-if="thread.message_count"
                        class="text-[11px] text-neutral-400"
                      >
                        · {{ thread.message_count }} msg
                      </span>
                    </template>
                  </div>
                </button>

                <button
                  type="button"
                  class="shrink-0 px-2 text-neutral-400 opacity-0 transition-opacity hover:text-neutral-700 group-hover/item:opacity-100 focus:opacity-100"
                  :class="{ 'opacity-100': menuFor === thread.thread_id }"
                  title="Options"
                  aria-label="Conversation options"
                  @click.stop="toggleMenu(thread.thread_id, $event)"
                >
                  <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <circle cx="4" cy="10" r="1.5" />
                    <circle cx="10" cy="10" r="1.5" />
                    <circle cx="16" cy="10" r="1.5" />
                  </svg>
                </button>
              </div>

              <!-- Options popover -->
              <div
                v-if="menuFor === thread.thread_id"
                class="absolute right-2 z-20 w-36 rounded-lg border border-neutral-200 bg-white py-1 shadow-lg"
                :class="menuDirection === 'up' ? 'bottom-9' : 'top-9'"
              >
                <button
                  type="button"
                  class="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-[13px] text-neutral-700 hover:bg-neutral-100"
                  @click.stop="startRename(thread)"
                >
                  Rename
                  <svg class="h-3.5 w-3.5 text-neutral-400" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
                    <path d="M13 4l3 3-8 8H5v-3l8-8z" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  class="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-[13px] text-red-600 hover:bg-red-50"
                  @click.stop="confirmDelete(thread)"
                >
                  Delete
                  <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
                    <path d="M5 6h10M8 6V4.5A1.5 1.5 0 019.5 3h1A1.5 1.5 0 0112 4.5V6m-5 0v9.5A1.5 1.5 0 008.5 17h3a1.5 1.5 0 001.5-1.5V6" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              </div>
            </li>
          </ul>
        </div>
      </template>
    </div>
  </aside>
</template>
